import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../context/ThemeContext';
import { DARK } from '../../utilities/constants';
import { getCurrentFormattedDate } from '../../utilities/commonFunctions';
import PlotCard from './PlotCard';
import { Spin } from 'antd';

const CHART_COLOR = '#ED0007';

const LEGEND_ITEMS = [
  {
    key: '',
    label: '',
    color: '',
  },
];

const DriverDrowsinessChart = ({ anomalyData, anomalyChartLoading }) => {
  const { theme } = useTheme();

  const [plotData, setPlotData] = useState([]);
  const [layout, setLayout] = useState({
    plot_bgcolor: theme === DARK ? '#000000' : '#EFF1F2',
    paper_bgcolor: 'transparent',
    font: { color: theme === DARK ? '#FFFFFF' : '#000000' },
    height: 350,
    margin: { l: 40, r: 30, t: 30, b: 30, padTop: 4 },
    xaxis: {
      gridcolor: '#2E3033',
      rangemode: 'tozero',
      fixedrange: true,
      ticklen: 8,
      automargin: true,
    },
    yaxis: {
      gridcolor: '#71767C',
      rangemode: 'tozero',
      fixedrange: true,
      ticklen: 16,
      automargin: true,
    },
    dragmode: false,
  });
  const [, setMaxValue] = useState(5);

  useEffect(() => {
    setLayout(prev => ({
      ...prev,
      font: { color: theme === DARK ? '#FFFFFF' : '#000000' },
      plot_bgcolor: theme === DARK ? '#000000' : '#EFF1F2',
    }));
  }, [theme]);

  const processData = dataPoints => {
    // Instead of using time as x, use index to ensure uniqueness
    const xValues = dataPoints.map((dp, idx) => idx); // unique numbers
    const tickLabels = dataPoints.map(dp => dp.time); // labels for ticks

    const yValues = dataPoints.map(dp => dp.value);

    const calculatedMax = Math.max(5, ...yValues) + 2;

    setMaxValue(calculatedMax);

    const zeroIdx = xValues.find(idx => tickLabels[idx] === '00:00');

    setLayout(prev => ({
      ...prev,
      xaxis: {
        ...prev.xaxis,
        tickvals: xValues,
        ticktext: tickLabels,
        categoryarray: xValues,
      },
      yaxis: {
        ...prev.yaxis,
        range: [0, calculatedMax],
      },
      shapes:
        zeroIdx !== undefined
          ? [
              {
                type: 'line',
                x0: zeroIdx,
                x1: zeroIdx,
                y0: 0,
                y1: 1.1,
                yref: 'paper',
                line: {
                  color: '#E552DA',
                  width: 1,
                  dash: 'solid',
                },
              },
            ]
          : [],
      annotations:
        zeroIdx !== undefined
          ? [
              {
                x: zeroIdx,
                y: 1.1,
                yref: 'paper',
                xref: 'x',
                xanchor: 'left',
                showarrow: false,
                text: getCurrentFormattedDate(),
                font: {
                  color: '#000000',
                  size: 12,
                  weight: 400,
                },
                bgcolor: '#E552DA',
                bordercolor: '#E552DA',
                borderwidth: 1,
                borderpad: 4,
                xshift: 4,
              },
            ]
          : [],
    }));

    setPlotData([
      {
        x: xValues,
        y: yValues,
        type: 'scatter',
        mode: 'markers',
        fill: 'tozeroy',
        fillcolor: `${CHART_COLOR}4D`,
        marker: { size: 10, color: CHART_COLOR },
        hovertemplate: '%{y}<extra></extra>',
      },
    ]);
  };

  const filterDmsData = data => {
    return data.map(item => ({
      time: item.time_period.split('-')[0].slice(0, 5),
      value: item.driver_drowsiness_count,
    }));
  };

  useEffect(() => {
    if (anomalyData) {
      processData(filterDmsData(anomalyData));
    }
  }, [anomalyData]);

  return (
    <Spin
      spinning={anomalyChartLoading}
      size="large"
      className="overview-chart-spinner"
    >
      <PlotCard
        title="Driver Drowsiness"
        plotData={plotData}
        layout={layout}
        legendItems={LEGEND_ITEMS}
      />
    </Spin>
  );
};

DriverDrowsinessChart.propTypes = {
  anomalyData: PropTypes.array,
  anomalyChartLoading: PropTypes.bool,
};

export default DriverDrowsinessChart;
